// KPIs › Priorities at `/c/{slug}/kpis/priorities` (PRIORITIES-HOME): the quarter's
// priorities, the chart's Keystones, as their own page. The quarter (`?quarter=`, absent = the
// current one), the grouping (`?group=team`), the filter (`?filter=mine|attention`) and a seat
// (`?seat=`, from the chart panel's "View all") are in the URL beside the focus. The page is
// open to whoever may open the scorecard OR the chart (`PrioritiesGate`): priorities are `core` and
// every member reads them on the chart, while the scorecard is a readers' page behind `kpi`; the
// server cuts the list to the Keystones audience either way. FOUNDER-OS phase 1: in Founder OS
// the page is the portfolio priorities with their cascade (was Portfolio › Goals); `?board=own`
// opens this board for the portfolio company's own priorities.
import { createRoute, useNavigate } from "@tanstack/react-router";
import { PortfolioGoalsScreen } from "../features/portfolio/portfolio-goals";
import {
  type PrioritiesFilter,
  PrioritiesGate,
  type PrioritiesGroup,
  PrioritiesScreen,
} from "../features/priorities/priorities-screen";
import { useCompany } from "../features/use-feature";
import { validateFocusSearch } from "../focus/focus";
import { kpisRoute } from "./c.$slug.kpis";

const QUARTER = /^\d{4}-Q[1-4]$/;
const ID = /^[0-9a-f-]{36}$/i;

export const prioritiesRoute = createRoute({
  getParentRoute: () => kpisRoute,
  path: "priorities",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...(typeof search.quarter === "string" && QUARTER.test(search.quarter)
      ? { quarter: search.quarter }
      : {}),
    ...(search.group === "team" ? { group: "team" as const } : {}),
    ...(search.filter === "mine" || search.filter === "attention"
      ? { filter: search.filter as PrioritiesFilter }
      : {}),
    ...(typeof search.seat === "string" && ID.test(search.seat) ? { seat: search.seat } : {}),
    ...(search.board === "own" ? { board: "own" as const } : {}),
  }),
  component: function Priorities() {
    const { api } = prioritiesRoute.useRouteContext();
    const { quarter, group, filter, seat, board } = prioritiesRoute.useSearch();
    const navigate = useNavigate();
    const company = useCompany();
    // FOUNDER-OS phase 1: in Founder OS, the portfolio priorities with their cascade; the
    // ordinary board (where they are set) is `?board=own`.
    if (company.kind === "portfolio" && board !== "own")
      return (
        <PrioritiesGate>
          <PortfolioGoalsScreen api={api} company={company} />
        </PrioritiesGate>
      );
    return (
      <PrioritiesGate>
        <PrioritiesScreen
          api={api}
          company={company}
          quarter={quarter}
          group={group ?? "owner"}
          filter={filter ?? "all"}
          seat={seat}
          onSearch={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => {
                const out: Record<string, unknown> = { ...prev };
                const put = (k: string, v: unknown, dflt?: unknown) => {
                  if (v === undefined || v === dflt) delete out[k];
                  else out[k] = v;
                };
                if ("quarter" in next) put("quarter", next.quarter);
                if ("group" in next) put("group", next.group, "owner" satisfies PrioritiesGroup);
                if ("filter" in next) put("filter", next.filter, "all");
                if ("seat" in next) put("seat", next.seat);
                return out;
              },
              replace: true,
            } as never)
          }
        />
      </PrioritiesGate>
    );
  },
});
